import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { OnScreenLoader } from '@ui';
import { useAuthContext } from '@lp/core/auth';
import CaseInformation from '@lp/features/RoutePlanner/components/CaseInformation/CaseRouteInformation';
import { LegList } from '@lp/features/RoutePlanner/components/RouteViewList/LegList';
import { RouteModals } from '@lp/features/RoutePlanner/components/RouteModals/RouteModals';
import { RoutePlannerMap } from '@lp/features/RoutePlanner/components/RouteMap/RoutePlannerMap';
import { RoutePlannerDataProvider } from '@lp/features/RoutePlanner/contexts/RoutePlannerDataContext';
import { useRouteBuilderState } from '@lp/features/RoutePlanner/store';
import { RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { useRouteModals } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { useCaseEventsSubHandler } from '@lp/core/hooks/useCaseEventsSubHandler';
import { CaseStatus, RandomizationResultType } from '@/api/apis/case';
import { LogisticsProjectionType, RouteTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { isAllowedStatus } from '@/utils/helpers';
import { useCaseByIdQuery, useCaseResourcesDataQuery, useCaseReviewsDataQuery } from '@/queries/case';
import { useLogisticsById } from '@/queries/route-planner';
import { getRouteLegsDivided } from '@/queries/route-planner/utils';
import { RouteBuilderPermissions } from '@/permissions';

export const LogisticsComponent = () => {
  const params = useParams();
  const { data: caseData, isFetched: isCaseFetched } = useCaseByIdQuery(params.caseId as string);
  const { data: caseResourcesData, isFetched: isCaseResourcesDataFetched } = useCaseResourcesDataQuery(
    params.caseId as string,
  );
  const { data: caseReviewsData, isFetched: isCaseReviewsDataFetched } = useCaseReviewsDataQuery(
    params.caseId as string,
  );
  const { data: logisticsProjection, isFetched: isLogisticsFetched } = useLogisticsById(
    caseData?.logisticsReference?.id,
  );

  const { onOpenModal } = useRouteModals();
  const { checkPermission } = useAuthContext();

  const isActionLoading = useRouteBuilderState.use.isActionLoading();
  const resetStore = useRouteBuilderState.use.resetStore();

  const [isSCSModalShow, setIsSCSModalShow] = useState(true);

  useCaseEventsSubHandler(params.caseId!, caseData?.logisticsReference?.id);

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, [resetStore]);

  const checkSCSLegs = useCallback(
    (logistics: LogisticsProjectionType) => {
      const hasPermissions = checkPermission(RouteBuilderPermissions.LegCreate);

      if (!hasPermissions || !isSCSModalShow || !caseData?.status) return;

      const isActiveCaseStatus = isAllowedStatus({
        statusFrom: CaseStatus.PendingLogistics,
        statusTo: CaseStatus.OrganOffOCS,
        currentStatus: caseData.status,
      });

      if (!isActiveCaseStatus) return;

      const route = logistics.routes.find((r) => r.routeType === RouteTypeEnum.Main);
      const isSCSResult = caseData?.randomization?.result === RandomizationResultType.SCS;
      const isMainRoute = route?.routeType === RouteTypeEnum.Main;

      if (route && isMainRoute && caseData?.trialProgram && isSCSResult) {
        const { legs: mainLegs } = getRouteLegsDivided(route.legs, logistics.locations);
        const dhToTxLegs = mainLegs.filter((leg) => leg.segmentType === SegmentTypeEnum.txcenter) || [];
        const hasSCSInLegs = dhToTxLegs.every((leg) => leg.hasScs);

        if (!hasSCSInLegs) {
          onOpenModal({
            type: RouteModalTypes.TrialRandomizationResult,
            context: null,
          });

          setIsSCSModalShow(false);
        }
      }
    },
    [
      caseData?.randomization?.result,
      caseData?.trialProgram,
      caseData?.status,
      onOpenModal,
      isSCSModalShow,
      checkPermission,
    ],
  );

  const isFetched = isCaseFetched && isCaseResourcesDataFetched && isCaseReviewsDataFetched && isLogisticsFetched;
  const isMissingRequiredData =
    !caseData?.routePlanningStatus || !caseResourcesData || !caseReviewsData || !logisticsProjection;

  useEffect(() => {
    if (isFetched && logisticsProjection) {
      checkSCSLegs(logisticsProjection);
    }
  }, [checkSCSLegs, isFetched, logisticsProjection]);

  if (!isFetched) {
    return <OnScreenLoader />;
  }

  if (isFetched && (isMissingRequiredData || !caseData)) {
    return caseData?.caseId ? (
      <div className="flex flex-col justify-center items-center w-full h-full overflow-hidden">
        {caseData?.caseId && (
          <Link to={`/case/${caseData?.caseId}`} className="text-base-5 underline">
            Return to case details
          </Link>
        )}
      </div>
    ) : null;
  }

  if (!caseData) return null;

  const { organType, donorHospitalDetails, donorHospitalTime, donorHospitalDate, donorHospitalTimeZone } = caseData;

  const showLegList =
    !!organType && !!donorHospitalDetails && !!donorHospitalTime && !!donorHospitalTimeZone && !!donorHospitalDate;

  return (
    <RoutePlannerDataProvider>
      {isActionLoading && <OnScreenLoader />}
      <div className="flex grow items-stretch w-full overflow-hidden">
        <div
          className="relative z-401 w-168.75 flex flex-col bg-white gap-4 items-start h-auto overflow-auto"
          style={{
            boxShadow: '4px 0px 9px 0px rgba(151, 152, 159, 0.2)',
          }}
        >
          <CaseInformation />
          {showLegList && <LegList />}
        </div>
        <div className="flex grow relative overflow-hidden">
          <RoutePlannerMap key={caseData.caseId} />
        </div>
      </div>
      <RouteModals />
    </RoutePlannerDataProvider>
  );
};
